/** Roles and permissions, shared so the client can hide what the server denies. */
export const ROLES = ['OWNER', 'STAFF', 'VIEWER', 'VIEWER_NO_DOWNLOAD'];
/**
 * What a person may do, split finely enough to say the things the client
 * actually asked for.
 *
 * Two of these are new and the split is the point of them. "Read" used to
 * carry taking a copy away and putting one back, which made a read-only
 * account either unable to print a statement or able to fill the Drive with
 * exports. They are separate rights now:
 *
 *   file:read      open it here, on screen
 *   file:download  take a copy to your own machine
 *   file:write     put something into the system — new, edited, or exported
 *
 * `file:write` is the one that draws the line the client drew twice: a Viewer
 * downloads to their PC "but not on the system", and does not get "convert to
 * PDF on system". Both are the same act — writing a file into the Drive — and
 * both are refused by the same permission. Reading a PDF to print it is not,
 * because it produces nothing that stays.
 *
 * Every role holds `file:download` today, so it separates nothing yet. It is
 * here because it is the line the client actually drew — "download it on their
 * pc but not on the system" — and because the alternative is `file:read`
 * quietly meaning both, which is how the question gets answered wrongly the
 * first time somebody wants a Viewer who cannot take copies home.
 *
 * There is deliberately no `file:print`. Every role may print, so it would
 * separate nothing and never will: printing is the browser printing a page it
 * has already been shown, and a permission that cannot be enforced and is
 * granted to everyone is a comment pretending to be a rule.
 */
export const PERMISSIONS = [
    // Drive
    'file:read',
    'file:download',
    'file:write',
    'file:delete',
    'file:share',
    // The rest of the workspace
    'dashboard:read',
    'session:read',
    // Administration
    'settings:read',
    'settings:write',
    'user:read',
    'user:write',
    'audit:read',
];
const ALL = PERMISSIONS;
export const ROLE_PERMISSIONS = {
    OWNER: ALL,
    /**
     * The people who do the work.
     *
     * Everything to do with the paperwork — open it, edit it, save it here or
     * to their own machine, print it. What they do not get is other people: who
     * exists, what role they hold, and the record of what everyone has been
     * doing.
     *
     * **The dashboard went on 18 Sep 2026**, and it had been here explicitly
     * before that, on the grounds that Staff are the ones who need to know what
     * is in the system. What changed is that Staff became sub-users with a Drive
     * of their own: they see the folders they were given and nothing else. The
     * dashboard and the storage page are facts about the *system* — totals,
     * what is on the disk, the largest items, what everyone opened last — and
     * none of it is scoped to a grant. Leaving them would have meant an account
     * that cannot open /Payroll being told how much of the disk it takes and
     * when it was last touched.
     *
     * Removing the permission closes the screens and the three endpoints behind
     * them in one move, which is the point of it being a permission rather than
     * a hidden link.
     *
     * `file:delete` is not among them, by the client's decision on 8 Sep 2026
     * after trying it: Staff should not be able to delete. The reasoning that
     * put it here — that managing a folder includes throwing it away, and it is
     * only the trash — was mine, not the client's, and an office where the
     * paperwork can only be removed by the person who owns the business is a
     * choice they are entitled to make.
     *
     * It also settles something that looked odd while Staff had it: a file
     * trashed by Staff appeared in the Owner's trash and not in theirs. That is
     * correct — there is one Drive and one trash, and the Owner is the one who
     * restores — but it reads as a file vanishing, which is its own argument
     * for not offering the button.
     *
     * No `session:read`. "Cannot view computer/system login history or audit
     * logs" is explicit, and the sign-in page is that history for this account.
     * It costs Staff the ability to spot and revoke a session they do not
     * recognise, which is a real loss and the client's call to make.
     */
    STAFF: ['file:read', 'file:download', 'file:write'],
    /**
     * Read, take a copy, print. Nothing that stays.
     *
     * No dashboard and no sign-in history: the client asked for an account that
     * sees the Drive and nothing else about the system. Changing their own
     * password and enrolling an authenticator is not on this list because it is
     * not a permission — it is their own account, and every role has it.
     */
    VIEWER: ['file:read', 'file:download'],
    /**
     * A Viewer who cannot take a copy away.
     *
     * The same account in every other respect — reads the Drive on screen,
     * changes nothing, saves nothing back — with the one right removed that
     * lets a file leave the building. For the papers that may be read at a desk
     * and not carried off it.
     *
     * It is friction, not a guarantee, and worth being plain about: anything on
     * a screen can be photographed, and the browser's own print dialog will
     * still make a PDF of a page it has been shown. What this stops is the
     * ordinary way a file leaves — the Download button — and it stops it
     * everywhere at once, on the row, in the viewer, and in a bulk zip.
     */
    VIEWER_NO_DOWNLOAD: ['file:read'],
};
export function can(role, permission) {
    return ROLE_PERMISSIONS[role].includes(permission);
}
export function canAny(role, permissions) {
    return permissions.some((permission) => can(role, permission));
}
export const ROLE_LABELS = {
    OWNER: 'Owner',
    STAFF: 'Staff',
    VIEWER: 'Viewer',
    VIEWER_NO_DOWNLOAD: 'Viewer (no downloads)',
};
export const ROLE_DESCRIPTIONS = {
    OWNER: 'Everything, including people, settings and the audit log.',
    STAFF: 'Works the paperwork: opens, edits, saves and prints everything, and sees the dashboard. Cannot delete, manage people, or read the audit log.',
    VIEWER: 'Reads the Drive, downloads to their own machine and prints. Saves nothing back, edits nothing, and sees nothing else about the system.',
    VIEWER_NO_DOWNLOAD: 'Reads the Drive on screen and prints. Cannot download, edit or save anything. For papers that are read at a desk and not carried off it.',
};
//# sourceMappingURL=roles.js.map